import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Storefront, CaretRight } from '@phosphor-icons/react';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { ProductCard } from './components/ProductCard';
import { getBrandLogo, getBrandBanner, getBrandLogoScale } from './components/brandAssets';
import { SoundWaveBg } from './components/SoundWaveBg';
import SeoHead from '../../components/SeoHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORE_SLUG = 'mrg';

const BrandPage = () => {
  const { brandSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addItem } = useCart();
  const { isInWishlist, toggleWishlist, fetchWishlistBySlug } = useWishlist();

  const [store, setStore] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoOk, setLogoOk] = useState(true);
  const [sort, setSort] = useState('newest');
  const [bgIdx, setBgIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API_URL}/api/s/${STORE_SLUG}`).then(r => setStore(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setLogoOk(true);
    window.scrollTo(0, 0);
    const type = new URLSearchParams(location.search).get('type');
    axios.get(`${API_URL}/api/s/${STORE_SLUG}/brands/${brandSlug}?sort=${sort}${type ? `&type=${encodeURIComponent(type)}` : ''}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [brandSlug, sort, location.search]);

  useEffect(() => { if (store?.id || store?._id) fetchWishlistBySlug?.(STORE_SLUG); }, [store]);

  const colors = useMemo(() => {
    const id = store?.identity || {};
    return { primary: id.primary_color || '#1B2A4A', accent: id.accent_color || '#C9A24B', dark: '#1F2933' };
  }, [store]);

  const brand = data?.brand;
  const products = data?.products || [];
  const logoUrl = brand ? (brand.logo_url || getBrandLogo(brand.name)) : null;
  const bannerBg = brand ? getBrandBanner(brand.slug || brand.name) : '';

  // Background = configured back-office image, else carousel of the brand's product photos
  const bgImages = useMemo(() => {
    if (brand?.banner_image) return [brand.banner_image];
    const imgs = products.map(p => p.featured_image).filter(Boolean);
    if (imgs.length) return imgs.slice(0, 10);
    return brand?.image ? [brand.image] : (bannerBg ? [bannerBg] : []);
  }, [products, brand, bannerBg]);

  useEffect(() => {
    setBgIdx(0);
    if (bgImages.length < 2) return undefined;
    const t = setInterval(() => setBgIdx(i => (i + 1) % bgImages.length), 4000);
    return () => clearInterval(t);
  }, [bgImages]);

  const cardProps = {
    colors, categories: [], getLink: (path) => path,
    isInWishlist, toggleWishlist, store, addItem, storeSlug: STORE_SLUG, isPro: false,
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-9 h-9 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: colors.primary }} />
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <p className="text-slate-500 mb-4">Marque introuvable.</p>
        <Link to="/" className="px-6 py-3 rounded-full text-white font-bold" style={{ backgroundColor: colors.primary }}>Retour à l&apos;accueil</Link>
      </div>
    );
  }

  return (
    <div className="bg-[#eef1f5] min-h-screen" data-testid="brand-page">
      <SeoHead
        title={`${brand.type_label ? brand.type_label.charAt(0).toUpperCase() + brand.type_label.slice(1) + ' ' : ''}${brand.name} | MRG Optique & Audition Istres`}
        description={`${brand.name} chez MRG Optique & Audition à Istres : ${brand.product_count} modèles${brand.type_label ? ' de ' + brand.type_label : ''}. Grandes marques, essayage en boutique, tiers payant mutuelles.`}
        image={brand.image}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Accueil", "item": (typeof window !== 'undefined' ? window.location.origin : '') + "/" },
              { "@type": "ListItem", "position": 2, "name": "Marques", "item": (typeof window !== 'undefined' ? window.location.origin : '') + "/marques" },
              { "@type": "ListItem", "position": 3, "name": brand.name, "item": typeof window !== 'undefined' ? window.location.href : undefined },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `${brand.name} — ${brand.product_count} modèles`,
            "description": `Collection ${brand.name} disponible chez MRG Optique & Audition à Istres.`,
            "url": typeof window !== 'undefined' ? window.location.href : undefined,
            "isPartOf": {
              "@type": "WebSite",
              "name": "MRG Optique & Audition",
              "url": typeof window !== 'undefined' ? window.location.origin : undefined,
            },
          },
        ]}
      />

      {/* ===== Immersive brand banner ===== */}
      <section className="relative overflow-hidden" style={{ height: 'clamp(300px, 42vh, 460px)' }} data-testid="brand-banner">
        {bgImages.map((src, i) => (
          <img key={i} src={src} alt="" loading="eager"
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
            style={{ opacity: i === bgIdx ? 1 : 0 }} />
        ))}
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${colors.dark}E6 0%, ${colors.primary}D9 55%, ${colors.primary}F2 100%)` }} />
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '22px 22px' }} />

        <div className="relative z-10 max-w-6xl mx-auto h-full px-6 flex flex-col justify-center items-center text-center">
          <nav className="absolute top-5 left-6 flex items-center gap-1.5 text-[12px] text-white/70" data-testid="brand-breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
            <CaretRight size={11} weight="bold" />
            <Link to="/marques" className="hover:text-white transition-colors">Marques</Link>
            <CaretRight size={11} weight="bold" />
            <span className="text-white font-semibold">{brand.name}</span>
          </nav>

          {logoUrl && logoOk ? (
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-full bg-white shadow-2xl mb-5 flex items-center justify-center overflow-hidden animate-fadeInUp">
              <img src={logoUrl} alt={brand.name} className="object-contain"
                style={{ maxWidth: '76%', maxHeight: '62%', transform: `scale(${getBrandLogoScale(brand.name)})` }}
                onError={() => setLogoOk(false)} />
            </div>
          ) : (
            <span className="mb-4 inline-block px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.3em] text-white/90 animate-fadeInUp"
              style={{ backgroundColor: colors.accent + '33', border: `1px solid ${colors.accent}66` }}>
              Collection
            </span>
          )}

          {brand.type_label && (
            <span className="mb-3 inline-block px-3.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.2em] text-white animate-fadeInUp"
              style={{ backgroundColor: colors.accent }}>
              {brand.type_label}
            </span>
          )}
          <h1 className="text-white font-semibold uppercase tracking-tight leading-none animate-fadeInUp"
            style={{ fontFamily: "'Poppins', sans-serif", fontSize: 'clamp(40px, 7vw, 86px)', textShadow: '0 6px 30px rgba(0,0,0,0.4)' }}>
            {brand.name}
          </h1>

          <div className="mt-4 flex items-center gap-3 text-white/85 animate-fadeInUp" style={{ animationDelay: '0.15s' }}>
            <span className="h-px w-10" style={{ backgroundColor: colors.accent }} />
            <span className="text-[14px] md:text-[15px] font-medium tracking-wide" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {brand.product_count} modèle{brand.product_count > 1 ? 's' : ''} disponible{brand.product_count > 1 ? 's' : ''}
            </span>
            <span className="h-px w-10" style={{ backgroundColor: colors.accent }} />
          </div>
        </div>
      </section>

      {/* ===== Toolbar + products on soft grey + sound waves ===== */}
      <section className="relative">
        <SoundWaveBg primary={colors.primary} accent={colors.accent} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between flex-wrap gap-3 py-5">
            <button onClick={() => navigate('/marques')}
              className="inline-flex items-center gap-2 text-[13px] font-semibold text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="back-to-brands">
              <ArrowLeft size={16} weight="bold" /> Toutes les marques
            </button>
            <div className="flex items-center gap-2">
              <span className="text-[12px] text-slate-400 font-medium">Trier&nbsp;:</span>
              <select value={sort} onChange={e => setSort(e.target.value)}
                className="text-[13px] font-semibold bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-2"
                style={{ '--tw-ring-color': colors.primary }} data-testid="brand-sort">
                <option value="newest">Nouveautés</option>
                <option value="price_asc">Prix croissant</option>
                <option value="price_desc">Prix décroissant</option>
                <option value="name">Nom (A-Z)</option>
              </select>
            </div>
          </div>

          {/* ===== Products grid ===== */}
          {products.length === 0 ? (
            <div className="py-24 text-center text-slate-400">
              <Storefront size={40} className="mx-auto mb-3 opacity-40" />
              Aucun produit disponible pour cette marque pour le moment.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 pb-16" data-testid="brand-products-grid">
              {products.map(p => (
                <ProductCard key={p.id} p={p} {...cardProps} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BrandPage;
